/**
 * Schema-driven inspector controls for parametric families.
 *
 * Renders a labeled control for each ParamDef:
 *   number → <input type="number"> with min/max/step + optional unit suffix
 *   enum   → <select>
 *   bool   → checkbox
 *
 * Adding a parameter to a family's schema makes a new control appear here
 * with zero extra UI code — this is the scalability linchpin.
 */
import type { ParamDef, ParamSchema, ParamValues } from '@/model/params'

interface Props {
  schema: ParamSchema
  params: ParamValues
  onChange: (key: string, value: number | string | boolean) => void
  /** If true, render in a compact two-column grid (default: single column). */
  compact?: boolean
}

export function ParamControls({ schema, params, onChange, compact }: Props) {
  if (schema.length === 0) return null
  return (
    <div className={compact ? 'param-grid' : 'param-list'}>
      {schema.map((def) => (
        <ParamField key={def.key} def={def} params={params} onChange={onChange} />
      ))}
    </div>
  )
}

function ParamField({
  def,
  params,
  onChange,
}: {
  def: ParamDef
  params: ParamValues
  onChange: (key: string, value: number | string | boolean) => void
}) {
  const value = params[def.key]

  if (def.type === 'number') {
    const unit = def.unit === 'in' ? '″' : def.unit === 'deg' ? '°' : def.unit === 'ft' ? '′' : ''
    return (
      <label className="field">
        <span className="field-label">{def.label}</span>
        <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
          <input
            className="num"
            type="number"
            step={def.step ?? 0.5}
            min={def.min}
            max={def.max}
            value={typeof value === 'number' ? value : def.default}
            onChange={(e) => {
              let v = Number(e.target.value)
              if (!Number.isNaN(v)) {
                if (def.min !== undefined) v = Math.max(def.min, v)
                if (def.max !== undefined) v = Math.min(def.max, v)
                onChange(def.key, v)
              }
            }}
          />
          {unit && <span style={{ color: '#7a849e', fontSize: 11 }}>{unit}</span>}
        </span>
      </label>
    )
  }

  if (def.type === 'enum') {
    return (
      <label className="field">
        <span className="field-label">{def.label}</span>
        <select
          className="num"
          value={typeof value === 'string' ? value : def.default}
          onChange={(e) => onChange(def.key, e.target.value)}
        >
          {def.options.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </select>
      </label>
    )
  }

  // bool
  return (
    <label className="field" style={{ flexDirection: 'row', alignItems: 'center', gap: 8 }}>
      <input
        type="checkbox"
        checked={typeof value === 'boolean' ? value : def.default}
        onChange={(e) => onChange(def.key, e.target.checked)}
      />
      <span className="field-label" style={{ margin: 0 }}>{def.label}</span>
    </label>
  )
}
